import type * as React from "react";
import { useKeyboardHints } from "../../hooks/use-keyboard-hints";
import { cn } from "../../lib/utils";

/**
 * A key cap. Renders nothing on a device with no keyboard to press it
 * (`useKeyboardHints`), so a tooltip reading "Close Esc" reads "Close" there
 * without the call site asking.
 */
export function Kbd({
  className,
  ...props
}: React.ComponentProps<"kbd">): React.ReactElement | null {
  if (!useKeyboardHints()) return null;
  return (
    <kbd
      className={cn(
        // A bordered chip with a light alpha fill, not a heavy solid wash:
        // `bg-foreground/15` read muddy on light surfaces. The
        // hairline `border` does the separating so the chip stays defined even
        // on the dark tooltip/popover (where an opaque fill within a few L
        // points of the surface would disappear); the faint `bg-foreground/5`
        // just lifts it a touch. Both are alpha, so they composite on every
        // surface in both modes.
        "pointer-events-none inline-flex h-5 min-w-5 select-none items-center justify-center gap-1 rounded-[.25rem] border border-border bg-foreground/5 px-1 font-medium font-sans text-secondary-foreground text-xs [&_svg:not([class*='size-'])]:size-3",
        className,
      )}
      data-slot="kbd"
      {...props}
    />
  );
}

export function KbdGroup({
  className,
  ...props
}: React.ComponentProps<"kbd">): React.ReactElement | null {
  if (!useKeyboardHints()) return null;
  return (
    <kbd
      className={cn("inline-flex items-center gap-1", className)}
      data-slot="kbd-group"
      {...props}
    />
  );
}
